if (!customElements.get('nc-set-functions')) {
  class NCSetFunctions extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-tab]')];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.highlights = [...this.querySelectorAll('[data-highlights]')];
      this.tabList = this.querySelector('[role="tablist"]');
      this.modal = this.querySelector('[data-included-modal]');
      this.sharedPurchase = this.modal?.querySelector('[data-unit-purchase]');
      this.cartPreview = this.querySelector('[data-cart-preview]');
      this.status = this.querySelector('[data-cart-status]');
      this.cartLines = this.querySelector('[data-cart-lines]');
      this.units = new Map([...this.querySelectorAll('[data-unit-variant-id]')].map(row => [
        Number(row.dataset.unitVariantId), row.dataset.unitSku
      ]));
      this.abort = new AbortController();
      if (!this.tabs.length || !this.modal) return;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index), {signal: this.abort.signal});
        tab.addEventListener('keydown', event => this.onTabKeydown(event, index), {signal: this.abort.signal});
      });
      this.querySelector('[data-open-pieces]').addEventListener('click', event => {
        if (this.sharedPurchase) this.sharedPurchase.open({opener: event.currentTarget});
        else {
          this.modal.open(event.currentTarget);
          this.loadCart();
        }
      }, {signal: this.abort.signal});
      if (!this.sharedPurchase) {
        this.cartPreview.addEventListener('click', event => {
          const button = event.target.closest('[data-remove-cart-line]');
          if (button) this.removeLine(button);
        }, {signal: this.abort.signal});
        for (const input of this.modal.querySelectorAll('.nc-set-functions__quantity input')) this.normalizeQuantity(input);
        this.modal.addEventListener('change', event => {
          if (event.target.matches('.nc-set-functions__quantity input')) this.normalizeQuantity(event.target);
        }, {signal: this.abort.signal});
        this.modal.addEventListener('focusout', event => {
          if (event.target.matches('.nc-set-functions__quantity input')) this.normalizeQuantity(event.target);
        }, {signal: this.abort.signal});
        this.modal.addEventListener('submit', event => {
          const input = event.target.querySelector('.nc-set-functions__quantity input');
          if (input) this.normalizeQuantity(input);
        }, {capture: true, signal: this.abort.signal});
      }

      const scene = this.querySelector('[data-scene]');
      let touchStart = null;
      scene.addEventListener('touchstart', event => {
        touchStart = [event.touches[0].clientX, event.touches[0].clientY];
      }, {passive: true, signal: this.abort.signal});
      scene.addEventListener('touchend', event => {
        if (!touchStart) return;
        const dx = event.changedTouches[0].clientX - touchStart[0];
        const dy = event.changedTouches[0].clientY - touchStart[1];
        touchStart = null;
        if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.3) {
          this.select((this.active + (dx < 0 ? 1 : this.tabs.length - 1)) % this.tabs.length);
        }
      }, {passive: true, signal: this.abort.signal});

      if (!this.sharedPurchase) {
        for (const name of ['on:cart:add', 'on:line-item:change']) {
          this.modal.addEventListener(name, event => this.onAddSuccess(event), {signal: this.abort.signal});
        }
        this.modal.addEventListener('on:cart:error', event => this.onAddError(event), {signal: this.abort.signal});
      }
      this.select(matchMedia('(min-width: 1024px)').matches && this.tabs.length > 1 ? 1 : 0);
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.cartFeedbackTimer);
      this.cartFeedbackIcon?.classList.remove('nc-set-functions-cart-added');
    }

    select(index, focus = false) {
      this.active = index;
      this.tabs.forEach((tab, position) => {
        const selected = position === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        this.panels[position].hidden = !selected;
        this.highlights[position].hidden = !selected;
      });
      const tab = this.tabs[index];
      if (focus) tab.focus();
      const container = this.tabList.getBoundingClientRect();
      const bounds = tab.getBoundingClientRect();
      if (bounds.left < container.left) this.tabList.scrollLeft -= container.left - bounds.left;
      else if (bounds.right > container.right) this.tabList.scrollLeft += bounds.right - container.right;
    }

    onTabKeydown(event, index) {
      let next;
      if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
      else if (event.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.tabs.length - 1;
      else return;
      event.preventDefault();
      this.select(next, true);
    }

    onAddSuccess(event) {
      const form = event.target.closest('product-form')?.querySelector('form');
      if (!form || !this.modal.contains(form)) return;
      this.cartLoadVersion = (this.cartLoadVersion || 0) + 1;
      if (event.detail?.cart) this.renderCartLines(event.detail.cart);
      else this.loadCart(true);
      const quantity = Number(form.querySelector('[name="quantity"]')?.value) || 1;
      this.setCartStatus(`${form.dataset.ncSetUnit} · ${quantity} added to cart.`);
      this.shakeCartIcon();
    }

    normalizeQuantity(input) {
      const min = Math.max(1, Number(input.min) || 1);
      const max = input.max ? Number(input.max) : Infinity;
      const step = Math.max(1, Number(input.step) || 1);
      const raw = Number(input.value);
      const safe = Number.isFinite(raw) ? Math.floor(raw) : min;
      const quantity = Math.min(max, min + Math.floor((Math.max(min, safe) - min) / step) * step);
      input.value = String(quantity);
      const control = input.closest('quantity-input');
      control.querySelector('[name="minus"]').disabled = quantity <= min;
      control.querySelector('[name="plus"]').disabled = quantity >= max;
    }

    onAddError(event) {
      const form = event.target.closest('product-form')?.querySelector('form');
      if (!form || !this.modal.contains(form)) return;
      this.setCartStatus(event.detail?.error || 'Unable to add this piece. Please try again.', true);
      setTimeout(() => form.querySelector('[name="add"]')?.classList.remove('is-success'), 0);
    }

    setCartStatus(message, error = false) {
      this.cartPreview.hidden = false;
      this.status.textContent = message;
      this.status.toggleAttribute('data-error', error);
    }

    renderCartLines(cart) {
      if (!Array.isArray(cart?.items)) return;
      this.cart = cart;
      this.cartLines.replaceChildren();
      for (const item of cart.items) {
        const sku = this.units.get(Number(item.variant_id));
        if (!sku || item.parent_relationship || item.properties?._petomg_bundle_id) continue;
        const line = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = `${sku} · Quantity: ${item.quantity}`;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.textContent = 'Remove';
        remove.setAttribute('aria-label', `Remove ${sku} from cart`);
        remove.dataset.removeCartLine = item.key;
        line.append(label, remove);
        this.cartLines.append(line);
      }
    }

    async fetchCart() {
      const response = await fetch(`${theme.routes.cart}.js`, {signal: this.abort.signal});
      if (!response.ok) throw new Error('Unable to load the cart.');
      return response.json();
    }

    async loadCart(preserveStatus = false) {
      const version = this.cartLoadVersion = (this.cartLoadVersion || 0) + 1;
      if (!preserveStatus) this.setCartStatus('Loading cart...');
      try {
        const cart = await this.fetchCart();
        if (version !== this.cartLoadVersion) return;
        this.renderCartLines(cart);
        if (!preserveStatus) this.setCartStatus(this.cartLines.childElementCount
          ? 'Individual pieces in your cart.' : 'No individual pieces added.');
      } catch {
        if (this.abort.signal.aborted || version !== this.cartLoadVersion) return;
        this.setCartStatus('Unable to load cart items. Please try again.', true);
      }
    }

    async removeLine(button) {
      if (this.removingLine) return;
      const key = button.dataset.removeCartLine;
      const item = this.cart?.items?.find(line => line.key === key);
      const sku = item && this.units.get(Number(item.variant_id));
      if (!sku || item.parent_relationship || item.properties?._petomg_bundle_id) {
        this.setCartStatus('This cart line has changed. Reopen the list and try again.', true);
        return;
      }

      this.removingLine = true;
      button.disabled = true;
      this.cartLoadVersion = (this.cartLoadVersion || 0) + 1;
      this.setCartStatus(`Removing ${sku}...`);
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender().map(section => section.section) || ['cart-icon-bubble'];
      let mutationApplied = false;
      try {
        if (drawer) await this.waitForDrawerLine(drawer, key, item.quantity);
        const response = await fetch(theme.routes.cartChange, {
          method: 'POST',
          headers: {'Accept': 'application/json', 'Content-Type': 'application/json'},
          body: JSON.stringify({id: key, quantity: 0, sections, sections_url: window.location.pathname})
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || cart.message || 'Unable to remove this piece.');
        mutationApplied = true;
        this.renderCartLines(cart);
        if (drawer && sections.every(section => typeof cart.sections?.[section] === 'string')) {
          await drawer.renderContents(cart, false);
        } else if (drawer) {
          await drawer.refresh(true);
        } else if (typeof cart.sections?.['cart-icon-bubble'] === 'string') {
          customElements.get('product-form')?.updateCartIcon(cart);
        }
        this.setCartStatus(this.cartLines.childElementCount ? 'Cart updated.' : 'No individual pieces added.');
        this.focusCartControl();
        this.dispatchEvent(new CustomEvent('on:line-item:change', {
          bubbles: true,
          detail: {cart, variantId: Number(item.variant_id), oldQuantity: item.quantity, newQuantity: 0}
        }));
      } catch {
        try {
          const current = await this.fetchCart();
          this.renderCartLines(current);
          if (drawer) await drawer.refresh(true);
          if (mutationApplied) {
            this.setCartStatus(this.cartLines.childElementCount ? 'Cart updated.' : 'No individual pieces added.');
            this.focusCartControl();
          } else {
            this.setCartStatus('Could not confirm removal. Cart refreshed; please review and retry.', true);
            this.focusCartControl(key);
          }
        } catch {
          this.setCartStatus('Cart status is uncertain. Please reopen the cart to check.', true);
        }
      } finally {
        this.removingLine = false;
        button.disabled = false;
      }
    }

    async waitForDrawerLine(drawer, key, quantity) {
      const hasLine = () => [...drawer.querySelectorAll('[data-line-key]')]
        .some(row => row.dataset.lineKey === key
          && Number(row.querySelector('.qty-input__input')?.value) === quantity);
      if (hasLine()) return;
      await new Promise(resolve => {
        const observer = new MutationObserver(() => {
          if (!hasLine()) return;
          observer.disconnect();
          clearTimeout(timeout);
          resolve();
        });
        observer.observe(drawer, {childList: true, subtree: true});
        const timeout = setTimeout(() => {
          observer.disconnect();
          resolve();
        }, 3000);
      });
      if (!hasLine()) await drawer.refresh(true);
      if (!hasLine()) throw new Error('The cart is still updating. Please try again.');
    }

    focusCartControl(key) {
      const buttons = [...this.cartLines.querySelectorAll('[data-remove-cart-line]')];
      const target = buttons.find(button => button.dataset.removeCartLine === key)
        || buttons[0]
        || this.modal.querySelector('.js-close-modal');
      target?.focus({preventScroll: true});
    }

    shakeCartIcon() {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const icon = document.getElementById('cart-icon');
      if (!icon) return;
      clearTimeout(this.cartFeedbackTimer);
      this.cartFeedbackIcon = icon;
      icon.classList.remove('nc-set-functions-cart-added');
      void icon.offsetWidth;
      icon.classList.add('nc-set-functions-cart-added');
      this.cartFeedbackTimer = setTimeout(() => icon.classList.remove('nc-set-functions-cart-added'), 1000);
    }
  }

  customElements.define('nc-set-functions', NCSetFunctions);
}
